import uuidv4 from 'uuid';
import { getLocalItem, setLocalItem } from '../common';
import { isEmpty } from '../utils/helpers';
import { EventProperties } from '../utils/mixpanel/mixpanel';
import { SearchMixpanelDataProps } from '../utils/mixpanel/searchView';

export const MIXPANEL_PROJECT_TOKEN =
  process.env.REACT_APP_MIXPANEL_PROJECT_TOKEN || '47162de9ff34457fff351ea3480cfda1';
export const MIXPANEL_DEBUG = process.env.REACT_APP_MIXPANEL_DEBUG === 'true';
export const MIXPANEL_ENABLE = process.env.REACT_APP_MIXPANEL_ENABLE === 'true';
export interface UserPropertyOnceProps {
  'Registration Date': string;
  'Activation Date': string;
  'First Purchase Date': string;
  'First Profile Completed Date': string;
  'First Offer Made Date': string;
  'First Offer Accepted Date': string;
  'First Message Reply Date': string;
  'First Phone Number Exchange Date': string;
  'Initial Platform': string;
}

export interface UserPropertyProps {
  $email: string;
  $name: string;
  GoogleSSO: boolean;
  Gender: string;
  'Want To': string;
  'Interested In': string;
  'Display Name': string;
  'Profile City': string;
  'Profile Country': string;
  DOB: string;
  'Body Type': string[];
  Height: string;
  Children: boolean;
  Smoking: boolean;
  Drinking: boolean;
  Education: string;
  Occupation: string;
  Income: string;
  Interests: string;
  'Email Verified': boolean;
  'Initial Profile Completed': boolean;
  'Hash Id': string;
  'Page Name'?: string;
  'Initial Platform'?: string;
}

export type TrackPackagesPageVisitedProps = {
  Source: string;
};

export type TrackProfileFavoritedProps = {
  Source: string;
  User: string;
};

export type TrackCreditsPurchasedProps = {
  Amount: number;
  Source: string;
  Total: string;
  AutoTopup: boolean;
  PaymentMethod?: string;
  PaymentSaved: boolean;
};

export type TrackProfileViewProps = {
  Source?: string;
  User: string;
};

export type TrackConversationUnlocked = {
  triggerToBuy: boolean;
};

export const MIXPANEL_USER_PROPERTIES_DEFAULT: Pick<UserPropertyProps, '$email' | 'GoogleSSO'> = {
  $email: '',
  GoogleSSO: false,
};

export const enum EVENT_NAMES {
  FIRST_STEP_COMPLETED = 'First Step Completed',
  SECOND_STEP_COMPLETED = 'Second Step Completed',
  CREDITS_PURCHASED = 'Credits Purchased',
  TUTORIAL_COMPLETED = 'Tutorial Completed',
  PROFILE_VIEW = 'Profile View',
  OFFER_MADE = 'Offer Made',
  CONVERSATION_UNLOCKED = 'Conv Unlocked',
  MESSAGE_TYPED = 'Message Typed',
  PAGE_VIEW = 'Page View',
  SEARCH_VIEW = 'Search View',
  PHOTO_STEP_COMPLETED = 'Photo Step Completed',
  OFFER_TUTORIAL_STEP_COMPLETED = 'Offer Tutorial Step Completed',
  PROFILE_WALL_STEP_1_SHOWN = 'Profile Wall Step 1 Shown',
  PROFILE_WALL_STEP_2_COMPLETED = 'Profile Wall Step 2 Completed',
  SMS_CODE_REQUESTED = 'SMS Code Requested',
  SMS_CODE_VERIFIED = 'SMS Code Verified',
  JOIN_PHONE_NUMBER_PROVIDED = 'Join - Phone Number Provided',
  PROFILE_DETAILS_STEP_1_COMPLETED = 'Profile Details Step 1 Completed',
  PROFILE_DETAILS_STEP_2_COMPLETED = 'Profile Details Step 2 Completed',
  JOIN_CLICKED_BACK = 'Join Clicked Back',
  USER_CLICKED_EMAIL_DOMAIN = 'User Clicked Email Domain',
  DISCOVER_MODE_ACTION = 'Discover Mode Action',
  PROFILE_CLOSED = 'Profile Closed',
  LIGHTBOX_CLOSED = 'Lightbox Closed',
  ACCOUNT_DELETION_STARTED = 'Account Deletion Started',
  ABUSE_REPORTED = 'Abuse Reported',
  BILLING_PAGE_VISITED = 'Billing Page Visited',
  BUY_VERIFICATION_PAGE_VIEW = 'Buy Verification Page View',
  DARK_MODE_ENABLED = 'Dark Mode Enabled',
  DEACTIVATION_PAGE_VIEWED = 'Deactivation Page Viewed',
  DEACTIVATE_OR_DELETE_MY_ACCOUNT_CLICKED = 'Deactivate Or Delete My Account Clicked',
  DELETE_PAGE_VIEWED = 'Delete Page Viewed',
  DISCOVER_HIT_ZERO = 'Discover Hit Zero',
  WAIT_TIME = 'Wait Time',
  VERIFICATION_EMAIL_RESENT = 'Verification Email Resent',
  USER_LOGGED_IN = 'User Logged In',
  TOGGLE_HIDE_PROFILE_VIEWS = 'Toggle - Hide profile views',
  TOGGLE_HIDE_MY_ONLINE_STATUS_LOGIN_DATE = 'Toggle - Hide my online status & login date',
  TOGGLE_HIDE_ME_FROM_SEARCH_RESULTS = 'Toggled - Hide me from Search Results',
  PWA_INSTALLED = 'PWA Installed',
  PWA_INSTALL_BANNER_SHOWN = 'PWA Install Banner Shown',
  ID_VERIFICATION_POPUP_OPENED = 'ID Verification Popup Opened',
  PROFILE_FAVORITED = 'Profile Favorited',
  DISCOVER_MODE_VIEWED = 'Discover Mode Viewed',
  PHONE_NUMBER_CHANGED = 'Phone Number Changed',
  EXPERIMENT_STARTED = '$experiment_started',
  LIGHTBOX_OPENED = 'Lightbox Opened',
  PACKAGES_VISITED = 'Packages Visited',
  OFFER_CANCELLED = 'Offer Cancelled',
  OFFER_IGNORED = 'Offer Ignored',
  OFFER_DECLINED = 'Offer Declined',
  ACCOUNT_NOT_FOUND = 'New Account from Non-Existing Email',
  PASSWORD_USED = 'Password Used',
  FORGOT_PASSWORD = 'Forgot Password',
  LOGIN_METHOD = 'Login Method',
  NEW_ACCOUNT = 'New Account',
  ACCOUNT_TYPE_HELP = 'Account Type Help',
  COOKIE_CONSENT_MODAL = 'Cookie Consent Modal',
}

export const TRACK_PAGE_VIEW_PATHS = {
  'dashboard-normal': 'Dashboard View', // not actual path
  'dashboard-swipe-mode': 'Discover Mode', // not actual path
  '/interests/my-favorites': 'My Favorites View',
  '/interests': 'Viewed me View',
  '/interests/favorited-me': 'Favorited Me View',
  '/offers/new': 'Received Requests View',
  '/offers/sent': 'Sent Offers View',
  '/credits': 'Purchase History',
  '/cancelled-offers': 'Cancelled Offers',
  '/blocked': 'Blocked Accounts',
  '/mail': 'Messages View',
  '/dismissed': 'Dismissed Accounts',
  menu: 'Menu',
  menu_notifications: 'Notifications',
  menu_privacy_safety: 'Privacy & Safety',
  menu_edit_profile: 'Profile Edit',
  '/search': 'Search',
  view_member_profile: 'Member Profile',
  terms: 'Terms',
  term_eu: 'Term EU',
  privacy: 'Privacy',
  faq: 'FAQ',
  blog: 'Blog',
  tips: 'Tips',
  guidelines: 'Guidelines',
  commitment: 'Commitment',
  facebook: 'Facebook',
  twitter: 'X',
  instagram: 'Instagram',
  'account-suspended': 'Account Suspended',
  'confirmed-fraud': 'Confirmed Fraud',
  'generic-error': 'Generic Error',
  'forgot-password-login': 'Forgot Password Login',
  'change-email-success': 'Change Email Success',
};

export const TRACK_PROFILE_VIEWS_FOR_OFFER_MADE = {
  profileViewDashboard: 'Profile Viewed from Dashboard',
  profileViewDiscover: 'Profile Viewed from Discover',
  profileViewSearch: 'Profile Viewed from Search',
  profileViewViewedMe: 'Profile Viewed from Viewed Me',
  profileViewReceivedRequest: 'Profile Viewed from Received Requests',
  profileViewReceivedOffers: 'Profile Viewed from Received Offers',
  profileViewMyFavorites: 'Profile Viewed from My Favorites',
  profileViewFavoritedMe: 'Profile Viewed from Favorited Me',
  profileViewDirect: 'Profile Direct',
};

export interface MixpanelDefaultStateProps {
  [EVENT_NAMES.CREDITS_PURCHASED]: TrackCreditsPurchasedProps;
  [EVENT_NAMES.CONVERSATION_UNLOCKED]: TrackConversationUnlocked;
  [EVENT_NAMES.SEARCH_VIEW]: SearchMixpanelDataProps;
  [EVENT_NAMES.DISCOVER_MODE_ACTION]: EventProperties[EVENT_NAMES.DISCOVER_MODE_ACTION];
  [EVENT_NAMES.OFFER_MADE]: {
    source: (typeof TRACK_PROFILE_VIEWS_FOR_OFFER_MADE)[keyof typeof TRACK_PROFILE_VIEWS_FOR_OFFER_MADE];
  };
  [EVENT_NAMES.PROFILE_CLOSED]: { 'Gallery Photos Seen': number };
  [EVENT_NAMES.LIGHTBOX_CLOSED]: { 'Lightbox Photos Seen': number };
}

export const MIXPANEL_DEFAULT_STATE: MixpanelDefaultStateProps = {
  [EVENT_NAMES.CREDITS_PURCHASED]: {
    Source: 'Direct URL',
    Amount: 0,
    Total: '',
    AutoTopup: false,
    PaymentSaved: false,
  },
  [EVENT_NAMES.CONVERSATION_UNLOCKED]: { triggerToBuy: false },
  [EVENT_NAMES.SEARCH_VIEW]: {
    Source: 'Direct Url',
    'Filter Applied': false,
    Parameters: ['Distance', 'Age', 'Height'],
  },
  [EVENT_NAMES.DISCOVER_MODE_ACTION]: {
    Result: '',
    'Profile Viewed': false,
    'Number of Photos Viewed': 1,
    'Offer Amount': 0,
  },
  [EVENT_NAMES.OFFER_MADE]: {
    source: TRACK_PROFILE_VIEWS_FOR_OFFER_MADE.profileViewDirect,
  },
  [EVENT_NAMES.PROFILE_CLOSED]: {
    'Gallery Photos Seen': 1,
  },
  [EVENT_NAMES.LIGHTBOX_CLOSED]: {
    'Lightbox Photos Seen': 1,
  },
};

export const interestedInForMixpanel = (userData: any): string => {
  if (userData.looking_female && userData.looking_male) {
    return 'both';
  }

  if (userData.looking_male) {
    return 'men';
  }

  if (userData.looking_female) {
    return 'women';
  }

  return 'both';
};

export const MIXPANEL_CREDITS_PURCHASED = 'mixpanel_credits_purchased';

export const ACCOUNT_TEMP_HASH_ID_LOCAL_STORAGE_KEY = 'wyp_account_temp_hash_id';
export const MIXPANEL_ANONYMOUS_TEMP_HASH_ID = 'wyp_account_mixpanel_temp_hash_id';

export function generateAccountTempHashId(): string {
  // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
  const tempHashId = uuidv4();

  setLocalItem(ACCOUNT_TEMP_HASH_ID_LOCAL_STORAGE_KEY, tempHashId);

  return tempHashId;
}

export function getAccountTempHashId(): string {
  const existingValues = getLocalItem(ACCOUNT_TEMP_HASH_ID_LOCAL_STORAGE_KEY);

  return !isEmpty(existingValues) ? existingValues : generateAccountTempHashId();
}
